import { useCallback, useMemo, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import candidatesServices, { NewTopicParams } from '@/services/candidates';
import InputText from '@/trio-ui/InputText';
import Modal from '@/trio-ui/Modal';
import { FetchApiIds } from '@/types';
import { CandidateStagesResponse, CandidateTopicType, PersonType, TopicType } from '@/types/api';
import CandidateNotesPAgeContentComponent from './CandidateNotesPageContent.component';

type Props = {
  candidate: PersonType;
  onRefetchCandidate?: () => void;
};

export const topicsOrder = [
  'Experience',
  'Technical Skills',
  'Cultural Fit',
  'Compensation & Availability',
];

const defaultNewTopicToAdd = {
  stageId: '',
  name: '',
  isModalOpen: false,
};

const CandidateNotesPageContent = ({ candidate, onRefetchCandidate }: Props): JSX.Element => {
  const toast = useToastContext();
  const [newTopicToAdd, setNewTopicToAdd] = useState(defaultNewTopicToAdd);
  const [autoScrollTopic, setAutoScrollTopic] = useState<string>();

  const candidateStagesResponse = useApiFetch<CandidateStagesResponse>(
    [FetchApiIds.CANDIDATE_STAGES, candidate.id],
    async () => candidatesServices.getCandidateStages(candidate.id)
  );

  const onAddNewNote = useCallback((): void => {
    candidateStagesResponse.refetch();
  }, [candidateStagesResponse]);

  const candidateStages = useMemo(() => {
    return candidateStagesResponse.data?.stages ?? [];
  }, [candidateStagesResponse.data]);

  const filterByTopicName = (
    topics: CandidateTopicType[],
    toCompare: string
  ): CandidateTopicType[] => {
    return topics.filter((topic) => topic.name === toCompare);
  };

  const candidateStagesOrdered = useMemo(() => {
    return candidateStages.map((stage) => {
      const firstTopic = filterByTopicName(stage.topics, topicsOrder[0]);
      const secondTopic = filterByTopicName(stage.topics, topicsOrder[1]);
      const thirdTopic = filterByTopicName(stage.topics, topicsOrder[2]);
      const fourthTopic = filterByTopicName(stage.topics, topicsOrder[3]);
      const notFilteredTopics = stage.topics
        .filter((a) => !topicsOrder.includes(a.name))
        .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));

      return {
        ...stage,
        topics: [
          ...firstTopic,
          ...secondTopic,
          ...thirdTopic,
          ...fourthTopic,
          ...notFilteredTopics,
        ],
      };
    });
  }, [candidateStages]);

  const onOpenModal = (stageId: string): void => {
    setNewTopicToAdd({ ...defaultNewTopicToAdd, stageId, isModalOpen: true });
  };

  const addNewTopicMutation = useApiSubmit<TopicType, NewTopicParams>(
    candidatesServices.addNewTopic
  );

  const onAddNewTopic = async (): Promise<void> => {
    try {
      const { name, stageId } = newTopicToAdd;
      const newTopic = await addNewTopicMutation.mutateAsync({
        id: candidate.id,
        stageId,
        name,
      });
      setAutoScrollTopic(newTopic.id);
      setNewTopicToAdd(defaultNewTopicToAdd);
      candidateStagesResponse.refetch();
      toast.setMessage('The new topic has been added.');
      toast.setSeverity('success');
      toast.showToast();
    } catch (err) {
      setNewTopicToAdd(defaultNewTopicToAdd);
      toast.setMessage('There was a problem adding the new topic.');
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  const onChangeTopicName = (event): void => {
    const {
      target: { value },
    } = event;

    setNewTopicToAdd((prev) => ({
      ...prev,
      name: value,
    }));
  };

  const onCloseModal = (): void => {
    setNewTopicToAdd(defaultNewTopicToAdd);
  };

  return (
    <>
      <CandidateNotesPAgeContentComponent
        onChangeCandidateField={onRefetchCandidate}
        onAddNewNote={onAddNewNote}
        candidateStages={candidateStagesOrdered}
        candidate={candidate}
        onOpenModal={onOpenModal}
        autoScrollTopic={autoScrollTopic}
        onAutoScroll={() => setAutoScrollTopic(undefined)}
      />
      <Modal
        modalId="new-topic-modal"
        isCentered
        size="sm"
        onConfirmClick={onAddNewTopic}
        modalTitle="Add a new topic"
        onConfirmLabel="Add the new topic"
        isButtonDisabled={newTopicToAdd?.name?.length <= 3}
        bodyRender={
          <InputText
            isLabelAboveField
            label="Topic Name"
            name="newtopic"
            value={newTopicToAdd.name}
            onChangeHandler={onChangeTopicName}
          />
        }
        onClose={onCloseModal}
        isOpen={newTopicToAdd.isModalOpen}
      />
    </>
  );
};

export default CandidateNotesPageContent;
